import {
    faArrowRightArrowLeft,
    faCaretRight,
    faPenToSquare,
    faShare,
    faShieldCheck,
    faShieldXmark,
    faSquareExclamation,
    faToggleOff,
    faToggleOn,
    faTrash
} from '@fortawesome/pro-solid-svg-icons';
import React, { useState } from 'react';


import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Can } from "../../../../../ability-context";
import { ALBUM_MEDIAS_KEY } from "../../../../../api/public-voissa/albums";
import { MEDIAS_KEY, MEDIA_KEY, deleteMedia, updateMedia } from "../../../../../api/public-voissa/medias";
import { USER_PROFILE_MEDIAS_KEY } from "../../../../../api/public-voissa/users";
import { MEDIA } from "../../../../../const/toasts";
import useReport, { REPORT_KINDS } from "../../../../../hooks/useReport";
import createLink from "../../../../../libs/createLink";
import { toastError, toastSuccess } from "../../../../../libs/toast";
import Alert from "../../../../components/Alert/Alert";
import MoveMedia from "../../../../components/MoveMedia";
import Report from "../../../../components/Report/Report";
import Drawer from '../../../Drawer/Drawer';
import MenuList, { MenuListItem } from '../../../MenuList/MenuList';
import MediaEdit from '../../../mediaEdit/MediaEdit';


const AdvancedMediaViewerOptions = ({media, handleClose = () =>{}, onlyRemove}) => {
    const queryClient = useQueryClient();
    const [toggleMediaEdit, setToggleMediaEdit] = useState(false);
    const [toggleReport, setToggleReport] = useState(false);
    const [toggleMoveCollection, setToggleMoveCollection] = useState(false);
    const [toggleAlertDelete, setToggleAlertDelete] = useState(false);
    const [loadingActionStatus, setLoadingActionStatus] = useState(false);
    const [loadingActionSFW, setLoadingActionSFW] = useState(false);
    const {handleReport} = useReport({parentKind : REPORT_KINDS.MEDIA, parentId : media?._id,onSuccess : () =>{setToggleReport(false)}});


    const {
        mutate : mutateUpdateMedia,
    } = useMutation((data) => updateMedia(media._id, data),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([USER_PROFILE_MEDIAS_KEY]);
            await queryClient.invalidateQueries([MEDIAS_KEY]);
            await queryClient.invalidateQueries([MEDIA_KEY, media._id]);
            setLoadingActionStatus(false);
            setLoadingActionSFW(false);
        },
        onError: () =>{
            toastError(MEDIA.ERROR.UPDATE);
            setLoadingActionStatus(false);
            setLoadingActionSFW(false);
        }
    });

    const {
        mutate : mutateDeleteMedia,
    } = useMutation((mediaId) => deleteMedia(mediaId),{
        onSuccess: async () =>{
            const queryCache = queryClient.getQueryCache()
            const queryKeys = queryCache.getAll().map(cache => cache.queryKey).filter(cache => cache[0] === USER_PROFILE_MEDIAS_KEY || cache[0] === MEDIAS_KEY || cache[0] === ALBUM_MEDIAS_KEY);
            queryKeys.map(async queryKey =>{
                await queryClient.invalidateQueries(queryKey);
            });
            setTimeout(async () =>{
                await queryClient.invalidateQueries([MEDIA_KEY, media._id]);
            }, 200);
            handleClose();
        },
        onError: () =>{
            toastError(MEDIA.ERROR.UPDATE);
        }
    });

    function handleDeletePicture() {
        mutateDeleteMedia(media._id);
        setToggleAlertDelete(false);
    }

    async function copyTextToClipboard() {
        const linkHref = window.location.href;
        toastSuccess("Lien de partage copié avec succès !");
        if ('clipboard' in navigator) {
            return await navigator.clipboard.writeText(linkHref);
        } else {
            return document.execCommand('copy', true, linkHref);
        }
    }

    return (
    <>
        <Drawer 
            headContent="Editer la publication"
            open={toggleMediaEdit} 
            handleClose={()=>setToggleMediaEdit(false)}>
            <MediaEdit currentMediaId={media._id}
                handleClose={()=>setToggleMediaEdit(false)}
                />
        </Drawer>
        <Drawer
            headContent="Signaler la publication"
            open={toggleReport}
            handleClose={()=>setToggleReport(false)}>
            <Report
                handleReport={handleReport}
                handleClose={()=>setToggleReport(false)}
            />
        </Drawer>
        <Drawer
            headContent="Changer de collection"
            open={toggleMoveCollection}
            handleClose={()=>setToggleMoveCollection(false)}
        >
            <MoveMedia
                handleMovePicture={()=> setToggleMoveCollection(false)}
                mediaId={media._id}
                handleClose={() => setToggleMoveCollection(false)}
                userId={media?.owner?._id}
                activeAlbumId={media?.album?._id}
            />
        </Drawer>
        <Drawer
            open={toggleAlertDelete}
            handleClose={()=>setToggleAlertDelete(false)}
            headContent={"Attention"}
        >
            <Alert text="Voulez-vous vraiment supprimer cette publication ?"
                   handleClose={() => setToggleAlertDelete(false)}
                   handleDelete={() => handleDeletePicture()}/>
        </Drawer>
        <MenuList style={{padding:'10px'}}>
            {!onlyRemove && (
                <>
                    <MenuListItem icon={faShare} onClick={copyTextToClipboard}>Partager</MenuListItem>
                    <Can action={"update"} subject={media} modelName="Media">
                        <MenuListItem icon={faPenToSquare} onClick={()=>setToggleMediaEdit(true)}>Editer</MenuListItem>
                    </Can>
                    <Can action={"move"} subject={media} modelName="Media">
                        <MenuListItem icon={faArrowRightArrowLeft} onClick={() => setToggleMoveCollection(true)}>Changer de collection</MenuListItem>
                    </Can>
                    {media.kind === "gallery" && (media.contentReview === 1 || media.sfwModerate) ? (
                        <Can action={"manage"} subject={media} modelName="Media">
                            <MenuListItem loading={loadingActionSFW} icon={media.sfw ? faShieldXmark : faShieldCheck} onClick={() =>{
                                setLoadingActionSFW(true);
                                mutateUpdateMedia({sfw : !media.sfw, sfwModerate : true});
                            }}>{
                                media.sfw
                                ? <>NSFW <FontAwesomeIcon icon={faCaretRight} style={{marginRight : "4px", marginLeft : "4px"}} /> SFW</>
                                : <>SFW <FontAwesomeIcon icon={faCaretRight} style={{marginRight : "4px", marginLeft : "4px"}} /> NSFW</>
                            }
                            </MenuListItem>
                        </Can>
                    ) : null}
                    {media.kind === "video" && ['guest', 'public'].includes(media.right) ? (
                        <Can action={"manage"} subject={media} modelName="Media">
                            <a href={`${createLink("tagModerationTreatmentVideo")}/${media?._id?.toString()}`}>
                                <MenuListItem icon={faShieldCheck}>Edit timeline</MenuListItem>
                            </a>
                        </Can>
                        ) : null}
                    <Can action={"status"} subject={media} modelName="Media">
                        <MenuListItem loading={loadingActionStatus} icon={media.status ? faToggleOn : faToggleOff} onClick={() => {
                            setLoadingActionStatus(true);
                            mutateUpdateMedia({status: !media.status})
                        }}>{media.status ? "Désactiver" : "Activer"}</MenuListItem>
                    </Can>
                    <MenuListItem icon={faSquareExclamation} onClick={() =>{setToggleReport(true)}}>Signaler</MenuListItem>
                </>
            )}
            <Can action={"delete"} subject={media} modelName="Media">
                <MenuListItem icon={faTrash} onClick={() => setToggleAlertDelete(true)}>Supprimer</MenuListItem>
            </Can>
        </MenuList>
    </>
  );
};

export default AdvancedMediaViewerOptions;
